import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash, FiCheck, FiX, FiToggleLeft, FiToggleRight, FiRefreshCw, FiFilter, FiChevronDown, FiChevronUp, FiArrowUp, FiArrowDown, FiMoreVertical, FiSettings, FiShield, FiAward, FiTrendingUp, FiActivity, FiBarChart2, FiPieChart, FiTarget, FiZap, FiClock, FiAlertCircle, FiGlobe, FiStar, FiCreditCard, FiPackage, FiShoppingCart, FiDollarSign, FiTrendingDown, FiDownload, FiUpload, FiEye2, FiEye, FiEyeOff, FiUsers, FiHome, FiTruck, FiNavigation, FiMail, FiPhone, FiCalendar, FiGift, FiLock, FiUnlock, FiCheckCircle, FiXCircle, FiTag, FiBriefcase } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: '',
    price: '',
    duration: 30,
    gstPercentage: 0,
    isGstInclude: false,
    // features: [],
    isActive: true
  });
  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
        toast.loading('Refreshing subscription plans...', { id: 'refresh' });
      }

      console.log('Fetching subscription plans...');
      const response = await api.get('/superadmin/subscription-plans');
      console.log('Subscription plans response:', response.data);
      
      if (response.data.success) {
        setPlans(response.data.data);
        
        // Show success message for manual refresh
        if (isManualRefresh) {
          toast.success(`Refreshed! Found ${response.data.data.length} plans`, { id: 'refresh' });
        }
      } else {
        toast.error('Failed to load subscription plans data', { id: 'refresh' });
      }
    } catch (error) {
      console.error('Fetch subscription plans error:', error);
      toast.error(`Failed to fetch subscription plans: ${error.response?.data?.message || error.message}`, { id: 'refresh' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    try {
      const response = selectedPlan
        ? await api.put(`/superadmin/subscription-plans/${selectedPlan._id}`, planForm)
        : await api.post('/superadmin/subscription-plans', planForm);

      if (response.data.success) {
        toast.success(selectedPlan ? 'Plan updated successfully' : 'Plan created successfully');
        setShowModal(false);
        resetForm();
        fetchPlans();
      }
    } catch (error) {
      toast.error('Failed to save plan');
    }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;

    try {
      const response = await api.delete(`/superadmin/subscription-plans/${planId}`);
      if (response.data.success) {
        toast.success('Plan deleted successfully');
        fetchPlans();
      }
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  const togglePlanStatus = async (plan) => {
    // Prevent deactivating Free plan
    if (plan.name.toLowerCase() === 'free' && plan.isActive) {
      toast.error('Free plan cannot be deactivated');
      return;
    }

    try {
      const response = await api.put(`/superadmin/subscription-plans/${plan._id}`, {
        ...plan,
        isActive: !plan.isActive
      });
      if (response.data.success) {
        toast.success(`Plan ${!plan.isActive ? 'activated' : 'deactivated'} successfully`);
        fetchPlans();
      }
    } catch (error) {
      toast.error('Failed to update plan status');
    }
  };

  const editPlan = (plan) => {
    setSelectedPlan(plan);
    setPlanForm({
      name: plan.name,
      price: plan.price,
      duration: plan.duration,
      gstPercentage: plan.gstPercentage || 0,
      isGstInclude: plan.isGstInclude || false,
      // features: plan.features,
      isActive: plan.isActive
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setPlanForm({
      name: '',
      price: '',
      duration: 30,
      gstPercentage: 0,
      isGstInclude: false,
      // features: [],
      isActive: true
    });
    setFeatureInput('');
    setSelectedPlan(null);
  };

  // const addFeature = () => {
  //   if (featureInput.trim()) {
  //     setPlanForm({
  //       ...planForm,
  //       features: [...planForm.features, featureInput.trim()]
  //     });
  //     setFeatureInput('');
  //   }
  // };

  // const removeFeature = (index) => {
  //   setPlanForm({
  //     ...planForm,
  //     features: planForm.features.filter((_, i) => i !== index)
  //   });
  // };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-flex items-center justify-center">
            <div className="w-20 h-20 border-4 border-indigo-200 rounded-full"></div>
            <div className="w-20 h-20 border-4 border-indigo-600 rounded-full animate-spin absolute border-t-transparent"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">Loading Subscription Plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <FiStar className="text-white text-xl" />
                </div>
                Subscription Plans
              </h1>
              <p className="text-gray-600 mt-1">Manage vendor subscription plans and pricing</p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold flex items-center gap-2">
                <FiStar className="text-amber-600" />
                {plans.length} Plans
              </div>
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="group relative px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-lg font-medium hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
              >
                <FiPlus className="text-sm group-hover:rotate-90 transition-transform duration-200" />
                <span className="text-sm font-medium">Create New Plan</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div key={plan._id} className={`group relative bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl hover:border-amber-300 ${!plan.isActive ? 'opacity-60' : ''}`}>
              {/* Plan Header */}
              <div className={`bg-gradient-to-r p-4 ${
                plan.name.toLowerCase() === 'free' 
                  ? 'from-gray-100 to-gray-200' 
                  : plan.name.toLowerCase() === 'basic' 
                    ? 'from-blue-100 to-blue-200'
                    : plan.name.toLowerCase() === 'premium'
                      ? 'from-purple-100 to-purple-200'
                      : 'from-amber-100 to-orange-200'
              }`}>
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      plan.name.toLowerCase() === 'free' 
                        ? 'bg-gray-500' 
                        : plan.name.toLowerCase() === 'basic' 
                          ? 'bg-blue-500'
                          : plan.name.toLowerCase() === 'premium'
                            ? 'bg-purple-500'
                            : 'bg-amber-500'
                    }`}>
                      {plan.name.toLowerCase() === 'free' ? (
                        <FiGift className="text-white text-sm" />
                      ) : plan.name.toLowerCase() === 'basic' ? (
                        <FiShield className="text-white text-sm" />
                      ) : plan.name.toLowerCase() === 'premium' ? (
                        <FiStar className="text-white text-sm" />
                      ) : (
                        <FiBriefcase className="text-white text-sm" />
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 capitalize">{plan.name}</h3>
                  </div>
                  {!plan.isActive && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold flex items-center gap-1">
                      <FiXCircle className="text-xs" />
                      Inactive
                    </span>
                  )}
                </div>
              </div>

              {/* Plan Content */}
              <div className="p-6">
                {/* Price Display */}
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-amber-600">₹</span>
                    <span className="text-3xl font-bold text-gray-900">{plan.price}</span>
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-1">
                    <FiClock className="text-gray-400 text-sm" />
                    <span className="text-sm text-gray-600">{plan.duration} days</span>
                  </div>
                  {plan.name.toLowerCase() === 'free' && (
                    <div className="mt-2">
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                        Forever Free
                      </span>
                    </div>
                  )}

                  {/* GST Information */}
                  {plan.name.toLowerCase() !== 'free' && plan.gstPercentage > 0 && (
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold flex items-center gap-1">
                        <span>GST {plan.gstPercentage}%</span>
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        plan.isGstInclude 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {plan.isGstInclude ? 'Included' : 'Extra'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button 
                    onClick={() => editPlan(plan)}
                    className="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 flex items-center justify-center gap-2 hover:shadow-md"
                  >
                    <FiEdit size={16} />
                    Edit Plan
                  </button>
                  
                  {/* Only show toggle for non-Free plans or if Free plan is inactive */}
                  {!(plan.name.toLowerCase() === 'free' && plan.isActive) && (
                    <button 
                      onClick={() => togglePlanStatus(plan)}
                      className={`w-full px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 hover:shadow-md ${
                        plan.isActive 
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700' 
                          : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                      }`}
                    >
                      {plan.isActive ? (
                        <>
                          <FiToggleRight size={16} />
                          Deactivate
                        </>
                      ) : (
                        <>
                          <FiToggleLeft size={16} />
                          Activate
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

      {/* Enhanced Empty State */}
          {plans.length === 0 && (
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-16 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FiStar className="text-amber-400 text-3xl" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No subscription plans found</h3>
              <p className="text-gray-500 mb-6">Create your first subscription plan to get started</p>
              <button 
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-orange-700 transition-all duration-200 hover:shadow-lg"
              >
                <FiPlus className="text-sm" />
                Create Your First Plan
              </button>
            </div>
          )}
        </div>

        {/* Enhanced Create/Edit Plan Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                      <FiStar className="text-white text-sm" />
                    </div>
                    {selectedPlan ? 'Edit Plan' : 'Create New Plan'}
                  </h2>
                  <button 
                    onClick={() => setShowModal(false)}
                    className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                  >
                    <FiX size={20} />
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreatePlan}>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Plan Name *</label>
                    <input
                      type="text"
                      value={planForm.name}
                      onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                      placeholder="e.g., Basic, Premium, Enterprise"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Price (₹) *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">₹</span>
                        <input
                          type="number"
                          value={planForm.price}
                          onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })}
                          placeholder="999"
                          min="0"
                          className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Duration (Days) *</label>
                      <input
                        type="number"
                        value={planForm.duration}
                        onChange={(e) => setPlanForm({ ...planForm, duration: e.target.value })}
                        placeholder="30"
                        min="1"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">GST Settings</label>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">GST Percentage (%)</label>
                          <input
                            type="number"
                            value={planForm.gstPercentage}
                            onChange={(e) => setPlanForm({ ...planForm, gstPercentage: e.target.value })}
                            placeholder="18"
                            min="0"
                            max="100"
                            step="0.1"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">GST Type</label>
                          <select
                            value={planForm.isGstInclude ? 'included' : 'extra'}
                            onChange={(e) => setPlanForm({ ...planForm, isGstInclude: e.target.value === 'included' })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          >
                            <option value="extra">Extra GST</option>
                            <option value="included">GST Included</option>
                          </select>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                        {planForm.isGstInclude ? 
                          'GST Included: GST amount is already included in the plan price' : 
                          'Extra GST: GST will be calculated and added to the plan price'
                        }
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => setPlanForm({ ...planForm, isActive: !planForm.isActive })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                          planForm.isActive ? 'bg-amber-500' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block w-4 h-4 transform transition-transform duration-200 ${
                          planForm.isActive ? 'translate-x-5' : 'translate-x-0'
                        }`}>
                          <span className="absolute left-0 top-0 h-full w-4 h-4 bg-white rounded-full transition-transform duration-200 shadow-md ${
                            planForm.isActive ? 'translate-x-5' : 'translate-x-0'
                          }"></span>
                        </span>
                      </button>
                      <span className="ml-3 text-sm font-medium text-gray-700">
                        {planForm.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t border-gray-200 flex gap-3">
                  <button 
                    type="button" 
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg font-medium hover:from-gray-600 hover:to-gray-700 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-orange-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2">
                    <FiStar className="text-sm group-hover:rotate-12 transition-transform" />
                    {selectedPlan ? 'Update Plan' : 'Create Plan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
    </div>
  );
};

export default SubscriptionPlans;
